import React, { useState } from 'react';
import styled from 'styled-components';
import { Gamepad2 } from 'lucide-react';

const FallbackDiv = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 12px;
  background: ${props => props.bgGradient || 'linear-gradient(135deg, #2a2e38 0%, #1e222a 100%)'};
  color: #ffffff;
  font-weight: 700;
  font-size: 14px;
  border-radius: 8px;
  box-sizing: border-box;
  text-shadow: 0px 2px 5px rgba(0,0,0,0.5);
  word-break: break-word;
  box-shadow: inset 0 0 20px rgba(0,0,0,0.3);
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
  text-transform: capitalize;

  /* Subtle overlay pattern for premium feel */
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background-image: radial-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px);
    background-size: 8px 8px;
    opacity: 0.4;
    pointer-events: none;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const IconWrapper = styled.div`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  border-radius: 50%;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  margin-bottom: 2px;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

const GRADIENTS = [
  'linear-gradient(135deg, #ff0844 0%, #ffb199 100%)', // Red Fire
  'linear-gradient(135deg, #09203f 0%, #537895 100%)', // Deep Space
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Neon Blue
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', // Neon Green
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', // Sunset
  'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', // Cyberpunk Purple
  'linear-gradient(135deg, #f83600 0%, #f9d423 100%)', // Blaze
  'linear-gradient(135deg, #b224ef 0%, #7579ff 100%)', // Royal Violet
  'linear-gradient(135deg, #16a085 0%, #f4d03f 100%)', // Emerald Gold
];

// Helper to generate consistent gradient based on game name string
const getGradientForName = (name) => {
  if (!name) return GRADIENTS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % GRADIENTS.length;
  return GRADIENTS[index];
};

const GameImage = ({ src, alt, className }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError || !src) {
    const bgGradient = getGradientForName(alt);
    return (
      <FallbackDiv className={className} bgGradient={bgGradient}>
        <ContentWrapper>
          <IconWrapper>
            <Gamepad2 size={24} color="#ffffff" strokeWidth={1.5} />
          </IconWrapper>
          {alt || 'Game'}
        </ContentWrapper>
      </FallbackDiv>
    );
  }

  return (
    <Image 
      src={src} 
      alt={alt} 
      className={className} 
      onError={() => setHasError(true)} 
    />
  );
};

export default GameImage;
